/**
 * Where the open light's tag hangs in the Banyan (§4.17). Held here rather
 * than in BanyanScene because it is arithmetic the eye cannot check by
 * looking — a tag one light too wide covers the canopy it is meant to name.
 *
 * Every length is scene units on the panned scene; `zoom` is scene units per
 * screen pixel, so the constants below are read in screen pixels and scaled
 * back in, the way the tag's own type is.
 */

/** The lantern tag's width, in screen pixels. */
export const LABEL_W = 272;
/** Screen pixels the floating nav takes over the box's top edge (the desktop pill row is 104px tall); no tag may climb into it. */
export const LABEL_TOP_PX = 108;
/** The clear air between a light and the tag beside it, in screen pixels. */
export const LABEL_GAP_PX = 18;
/** The narrowest a tag may be squeezed to still hang beside a light rather than under it (screen pixels: below this the title wraps to a ribbon and the doors stack). */
export const LABEL_SIDE_MIN_PX = 200;

export type LabelSide = 'left' | 'right' | 'below';

export interface LabelBox { x: number; y: number; w: number; side: LabelSide }

/**
 * 2026-09-06: the tag hangs BESIDE the light, on the side with room for it —
 * next to what was touched, not lying over the canopy under it. Both sides
 * free: the one covering fewer other lights wins, then the roomier one. A side
 * takes what room it has (down to LABEL_SIDE_MIN_PX, the tag wrapping into a
 * narrower column); when neither side holds that much — a phone's narrow
 * strip, a light near the trunk — the tag falls back under the light, where it
 * always hung. It is always inside the visible strip and always below the nav.
 *
 * `cx`/`cy`: the light's place after the narrowing and the pan. `h`: the tag's
 * measured height. `others`: every other light, same coordinates.
 */
export function layoutLabel(args: {
  cx: number; cy: number; h: number; zoom: number;
  /** The scene row the tag may not hang below (the ground line). */
  bottom: number;
  /** The visible strip of the scene: its left edge, width, and top row. */
  view: { x0: number; w: number; y0: number };
  others: Array<{ x: number; y: number }>;
  /** Screen pixels of the strip's top edge the tag may not climb into: whatever
   *  floats over the surface's top — the homepage's nav by default; a place
   *  page's sky passes the height of its own Back / Home row. */
  topInset?: number;
}): LabelBox {
  const { cx, cy, h, zoom, bottom, view, others, topInset = LABEL_TOP_PX } = args;
  const pad = 8 * zoom;
  const gap = LABEL_GAP_PX * zoom;
  const full = LABEL_W * zoom;
  const minX = view.x0 + pad;
  const maxX = view.x0 + view.w - pad;
  const top = view.y0 + topInset * zoom;
  // Beside means level with: the tag is centred on the light it names.
  const y = Math.max(top, Math.min(bottom - pad - h, cy - h / 2));
  const least = Math.min(full, LABEL_SIDE_MIN_PX * zoom);
  const roomR = maxX - (cx + gap);
  const roomL = (cx - gap) - minX;
  const sides: Array<{ side: LabelSide; x: number; w: number }> = [];
  if (roomR >= least) sides.push({ side: 'right', x: cx + gap, w: Math.min(full, roomR) });
  if (roomL >= least) { const w = Math.min(full, roomL); sides.push({ side: 'left', x: cx - gap - w, w }); }
  if (sides.length === 0) {
    const w = Math.min(full, maxX - minX);
    // Held off the bottom the way the two sides are: a frame that clips what
    // leaves it (a place page's band) would otherwise cut the doors off a tag
    // under a low light, and the homepage would print one over its ground.
    // Only a frame shorter than the tag itself gives up, top edge first.
    const y = Math.max(top, Math.min(bottom - pad - h, cy + 14 * zoom));
    return { x: Math.max(minX, Math.min(maxX - w, cx - w / 2)), y, w, side: 'below' };
  }
  const slack = 6 * zoom;
  const covers = (c: { x: number; w: number }): number => others
    .filter((o) => o.x > c.x - slack && o.x < c.x + c.w + slack && o.y > y - slack && o.y < y + h + slack).length;
  sides.sort((a, b) => (covers(a) - covers(b)) || (b.w - a.w));
  return { ...sides[0], y };
}
